import { computed, type ComputedRef, type Ref } from 'vue'
import { assertNever } from '@/helpers'
import { parseStringTemplate } from '@/helpers/parseStringTemplate'

import type {
    CatalogueEngagementRingsFilterInput,
    CatalogueWeddingRingFilterInput,
    EngagementRingFiltersFragment,
    FacetedContentProductLabelTranslationFragment,
    GemsApi_StoneFilters,
    GemsBoundsFragment,
    MetaFragment,
    MetalFilterFragment,
    ProductPageFacetedContentFragment,
    ProductPageFacetedContentLongDescriptionBlockFragment,
    ProductPageFacetedContentLongDescriptionFragment,
    WeddingRingFiltersFragment,
} from '@/graphql/api'
import { compareMetalFilterFragmentToMetalInput } from './ring/engagement'
import { capitalize } from '@/helpers/string-case'
import { capitalCase } from 'change-case-all'
import type { JewelleryProductType } from '@/router'
import { getJewelleryProductTypeLabel } from '@/views/product/jewellery/getJewelleryProductTypeLabel'
import type { Filters } from '@/views/product/jewellery/Jewelleries.vue'

export type FacetedContentProduct =
    | {
          type: 'engagement-rings'
          filters?: EngagementRingFiltersFragment
          filterValues: CatalogueEngagementRingsFilterInput
      }
    | {
          type: 'wedding-rings'
          filters?: WeddingRingFiltersFragment
          filterValues: CatalogueWeddingRingFilterInput
      }
    | {
          type: 'jewelleries'
          filters?: Filters['available']
          filterValues: Filters['selected'] | undefined
          productType: JewelleryProductType | undefined
      }
    | {
          type: 'diamond-search'
          filters?: GemsBoundsFragment | null
          filterValues?: GemsApi_StoneFilters
      }

const mapMetalNumericPredicate = ({ metal }: MetalFilterFragment) =>
    metal.translation.name

const mapMetalTextPredicate = ({ metal }: MetalFilterFragment) =>
    `${metal.color?.translation.name || ''} ${metal.element.translation.name}`.trim()

const getSelectedMetals = (
    filters:
        | EngagementRingFiltersFragment
        | WeddingRingFiltersFragment
        | Filters['available'],
    filterValues:
        | CatalogueEngagementRingsFilterInput
        | CatalogueWeddingRingFilterInput
        | Filters['selected']
        | undefined
) =>
    filters.primaryMetals.filter((metal) =>
        filterValues?.primaryMetalCodes?.find((val) =>
            compareMetalFilterFragmentToMetalInput(metal, val)
        )
    )

const mapUniqueJoinLowerCase = <T>(arr: T[], mapFn: (item: T) => string) =>
    arr
        .map(mapFn)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(' ')
        .toLocaleLowerCase()

const parseFacetedContentTemplateEngagementRings = (
    template: string,
    filters: EngagementRingFiltersFragment | undefined,
    filterValues: CatalogueEngagementRingsFilterInput,
    productStringParsed?: string
) => {
    if (!filters) {
        return ''
    }

    const metals = getSelectedMetals(filters, filterValues)

    return parseStringTemplate(template, {
        design_style: mapUniqueJoinLowerCase(
            filters.designStyles.filter((style) =>
                filterValues.designStyles?.includes(style.label.systemCode)
            ),
            (style) => getName(style.label.translation)
        ),
        numeric_metal: numericMetals(metals),
        text_metal: textMetals(metals),
        gem_shape: mapUniqueJoinLowerCase(
            filters.centreStoneShapes.filter((shape) =>
                filterValues.centreStoneShapes?.includes(shape.shape.systemCode)
            ),
            (shape) => shape.shape.translation.name
        ),
        gem_type: 'diamond',
        ring_style: mapUniqueJoinLowerCase(
            filters.ringStyles.filter((style) =>
                filterValues.ringStyles?.includes(style.label.systemCode)
            ),
            (style) => getName(style.label.translation)
        ),
        product_string: productStringParsed || '',
    })
}

const parseFacetedContentTemplateWeddingRings = (
    template: string,
    filters: WeddingRingFiltersFragment,
    filterValues: CatalogueWeddingRingFilterInput,
    productStringParsed?: string
) => {
    const metals = getSelectedMetals(filters, filterValues)

    return parseStringTemplate(template, {
        numeric_metal: numericMetals(metals),
        text_metal: textMetals(metals),
        category: mapUniqueJoinLowerCase(
            filters.categories.filter((category) =>
                filterValues.categories?.includes(category.label.systemCode)
            ),
            (category) => getName(category.label.translation)
        ),
        ring_style: mapUniqueJoinLowerCase(
            filters.ringStyles.filter((style) =>
                filterValues.ringStyles?.includes(style.label.systemCode)
            ),
            (style) => getName(style.label.translation)
        ),
        product_string: productStringParsed || '',
    })
}

const parseFacetedContentTemplateDiamondSearch = (
    template: string,
    filters: GemsBoundsFragment,
    filterValues?: GemsApi_StoneFilters,
    productStringParsed?: string
) => {
    if (!filterValues) {
        return ''
    }

    return parseStringTemplate(template, {
        gem_shape: mapUniqueJoinLowerCase(
            filters.shapes.filter((shape) =>
                filterValues.shape?.includes(shape.code)
            ),
            (shape) => shape.info?.translation.name ?? shape.code
        ),
        product_string: productStringParsed || '',
    })
}

const numericMetals = (metals: MetalFilterFragment[]): string =>
    metals.length > 1
        ? `${metals[0]?.metal.color?.translation.name || ''} ${metals[0]?.metal.element.translation.name || ''}`.trim()
        : mapUniqueJoinLowerCase(metals, mapMetalNumericPredicate)

const textMetals = (metals: MetalFilterFragment[]): string =>
    mapUniqueJoinLowerCase(metals, mapMetalTextPredicate)

const getName = (
    translation: FacetedContentProductLabelTranslationFragment
): string => {
    return translation.facetedContentName ?? translation.name
}

const parseFacetedContentTemplateJewelleries = (
    template: string,
    filters: Filters['available'],
    filterValues: Filters['selected'] | undefined,
    productStringParsed?: string,
    productType?: JewelleryProductType
) => {
    const metals = getSelectedMetals(filters, filterValues)

    const productStylesAvailable =
        'productStyles' in filters ? filters.productStyles : undefined
    const productStylesValues =
        filterValues && 'productStyles' in filterValues
            ? filterValues.productStyles
            : undefined

    return parseStringTemplate(template, {
        gemstone: mapUniqueJoinLowerCase(
            filters.gemstones.filter((style) =>
                filterValues?.gemstones?.includes(style.label.systemCode)
            ),
            (style) => getName(style.label.translation)
        ),
        design_style: mapUniqueJoinLowerCase(
            filters.designStyles.filter((style) =>
                filterValues?.designStyles?.includes(style.label.systemCode)
            ),
            (style) => getName(style.label.translation)
        ),
        product_style: mapUniqueJoinLowerCase(
            productStylesAvailable && productStylesValues
                ? productStylesAvailable.filter((style) =>
                      productStylesValues.includes(
                          /** @ts-expect-error alabala */
                          style.label.systemCode
                      )
                  )
                : [],
            (style) => getName(style.label.translation)
        ),
        category: productType
            ? getJewelleryProductTypeLabel(
                  productType
              ).translation.name.toLocaleLowerCase()
            : 'jewellery',
        numeric_metal: numericMetals(metals),
        text_metal: textMetals(metals),
        product_string: productStringParsed || '',
    })
}

export interface FacetedContentContentInterface {
    title: string | undefined
    description: string | undefined
    longDescriptionBlocks:
        | ProductPageFacetedContentLongDescriptionBlockFragment['blocks']
        | undefined
    longDescriptionText: string | undefined
}

const isBlocksDescription = (
    longDescription: ProductPageFacetedContentLongDescriptionFragment
): longDescription is ProductPageFacetedContentLongDescriptionBlockFragment =>
    'blocks' in longDescription

export interface FacetedContent {
    facetedMetaTitle: ComputedRef<string | undefined>
    facetedMetaDescription: ComputedRef<string | undefined>
    facetedTitle: ComputedRef<string | undefined>
    facetedContent: ComputedRef<FacetedContentContentInterface | undefined>
}

export function useFacetedContent(
    product: Readonly<Ref<FacetedContentProduct>>,
    content: Readonly<
        Ref<ProductPageFacetedContentFragment | null | undefined>
    >,
    activeFilterCount: Readonly<Ref<number | undefined>>,
    meta: Readonly<Ref<MetaFragment | undefined>>
): FacetedContent {
    function parse(
        product: FacetedContentProduct,
        template: string,
        productStringParsed?: string
    ) {
        if (!product.filters) {
            return ''
        }

        switch (product.type) {
            case 'engagement-rings':
                return parseFacetedContentTemplateEngagementRings(
                    template,
                    product.filters,
                    product.filterValues,
                    productStringParsed
                )
            case 'wedding-rings':
                return parseFacetedContentTemplateWeddingRings(
                    template,
                    product.filters,
                    product.filterValues,
                    productStringParsed
                )
            case 'jewelleries':
                return parseFacetedContentTemplateJewelleries(
                    template,
                    product.filters,
                    product.filterValues,
                    productStringParsed,
                    product.productType
                )
            case 'diamond-search':
                return parseFacetedContentTemplateDiamondSearch(
                    template,
                    product.filters,
                    product.filterValues,
                    productStringParsed
                )
            default:
                return assertNever(product, '')
        }
    }

    const productStringParsed = computed(() => {
        const productString = content.value?.productString

        if (!productString) {
            return undefined
        }

        return parse(product.value, productString)
    })

    const parseFacetedContentTemplate = (template: string) =>
        parse(product.value, template, productStringParsed.value)

    const facetedTitle = computed(() => {
        if (!content.value) {
            return undefined
        }

        if (activeFilterCount.value === 0) {
            return undefined
        }

        return capitalCase(
            parseFacetedContentTemplate(content.value.contentTitle)
        )
    })

    const facetedMetaTitle = computed(() => {
        if (activeFilterCount.value === 0) {
            return meta.value?.title
        }
        return content.value
            ? capitalCase(parseFacetedContentTemplate(content.value.meta.title))
            : meta.value?.title
    })

    const facetedMetaDescription = computed(() => {
        if (activeFilterCount.value === 0) {
            return meta.value?.description
        }

        return content.value
            ? capitalize(
                  parseFacetedContentTemplate(content.value.meta.description)
              )
            : meta.value?.description
    })

    function getContentLongDescriptionBlocks(
        description:
            | ProductPageFacetedContentFragment['longDescription']
            | undefined
    ):
        | ProductPageFacetedContentLongDescriptionBlockFragment['blocks']
        | undefined {
        if (!description) {
            return undefined
        }

        if (!isBlocksDescription(description)) {
            return undefined
        }

        return description.blocks
    }

    function getContentLongDescriptionText(
        description:
            | ProductPageFacetedContentFragment['longDescription']
            | undefined
    ): string | undefined {
        if (!description) {
            return undefined
        }

        if (isBlocksDescription(description)) {
            return undefined
        }

        return parseFacetedContentTemplate(description.text)
    }

    const facetedContent = computed(
        (): FacetedContentContentInterface | undefined => {
            if (activeFilterCount.value === 0) {
                return undefined
            }

            const ct = content.value

            if (!ct) {
                return undefined
            }

            const title = parseFacetedContentTemplate(ct.title)
            const description = parseFacetedContentTemplate(ct.description)

            return {
                title,
                description,
                longDescriptionBlocks: getContentLongDescriptionBlocks(
                    ct.longDescription
                ),
                longDescriptionText: getContentLongDescriptionText(
                    ct.longDescription
                ),
            }
        }
    )

    return {
        facetedMetaTitle,
        facetedMetaDescription,
        facetedTitle,
        facetedContent,
    }
}
